import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import TemplateService from '../../../Services/TemplateService';
import TemplateVariantItem from '../../../Domain/Template/TemplateVariantItem';

// The one window of the Templates page: every action edits a Template, so which fields show and
// which call is made both follow the ShownPanel value the page spreads onto the selected item.
export default class TemplateWindowDescription {
  // Mirrors TemplateConstants.StringContentTypes; only pre-fills the box, the backend re-infers
  static stringContentTypes = ['HTML'];

  // Mirrors TemplateConstants.AcceptedTypes
  static acceptedTypes = '.html, .xlsx, .xlsm, .docx';

  static itemType = TemplateVariantItem;

  static inferIsStringContent(fileName) {
    if (!fileName || fileName.indexOf('.') < 0) {
      return false;
    }
    const extension = fileName.split('.').pop();
    return TemplateWindowDescription.stringContentTypes.includes(extension.trim().toUpperCase());
  }

  // Every field's `hide` tests which panel the action opened
  static shownIn(...panels) {
    return (item) => !panels.includes(item.ShownPanel);
  }

  // Same insert as creating a template, with the name already decided
  static isAddingLanguage(item) {
    return item.ShownPanel === ShownPanelsEnum.AddNewItem && !!item.Name;
  }

  static isNamingNewTemplate(item) {
    return item.ShownPanel === ShownPanelsEnum.AddNewItem && !item.Name;
  }

  static getTitle(item) {
    if (item.ShownPanel === ShownPanelsEnum.EditItem) {
      return 'Edit Template';
    }
    if (item.ShownPanel === ShownPanelsEnum.FileUpload) {
      return 'Upload New Version';
    }
    return TemplateWindowDescription.isAddingLanguage(item) ? 'Add Language' : 'Create Template';
  }

  static getSuccessMessage(item) {
    if (item.ShownPanel === ShownPanelsEnum.EditItem) {
      return 'The template has been saved.';
    }
    if (item.ShownPanel === ShownPanelsEnum.FileUpload) {
      return 'The new version has been stored.';
    }
    return TemplateWindowDescription.isAddingLanguage(item)
      ? 'The language has been added to the template.'
      : 'The template has been created.';
  }

  static titleForCreate = TemplateWindowDescription.getTitle;
  static titleForEdit = TemplateWindowDescription.getTitle;
  static finishButtonLabel = 'Save';
  static messageOnSuccess = TemplateWindowDescription.getSuccessMessage;

  // MUST return the promise (wizard contract).
  static serviceFunction = (edited) => {
    if (edited.ShownPanel === ShownPanelsEnum.EditItem) {
      return TemplateService.UpdateTemplateGroup(edited);
    }
    if (edited.ShownPanel === ShownPanelsEnum.FileUpload) {
      return TemplateService.UploadTemplateVersion(edited);
    }
    return TemplateService.CreateTemplate(edited);
  };

  static pagesInGeneralEditWizzard = [
    {
      label: 'Template',
      fields: [
        {
          fieldName: 'Name',
          label: 'Template Name',
          className: 'col-12',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.forRequiredTextField,
          hide: (item) => !TemplateWindowDescription.isNamingNewTemplate(item),
        },
        {
          // TextReadOnly, not `disabled`: a disabled field renders an empty placeholder
          fieldName: 'Name',
          label: 'Template Name',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          hide: (item) => TemplateWindowDescription.isNamingNewTemplate(item),
        },
        {
          // The template's description, so a new language inherits it instead of entering one
          fieldName: 'Description',
          label: 'Description',
          className: 'col-12',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
          hide: (item) =>
            !TemplateWindowDescription.isNamingNewTemplate(item) && item.ShownPanel !== ShownPanelsEnum.EditItem,
        },
        {
          // A new template starts in the default language, and a row's language is fixed after that
          fieldName: 'Locale',
          label: 'Language',
          className: 'col-12',
          type: InputTypesEnum.ApolloLanguageLocaleSelect,
          validityChecker: ValidityHelper.forRequiredSelectField,
          hide: (item) => !TemplateWindowDescription.isAddingLanguage(item),
        },
        {
          // A new version keeps the language it is a version of.
          fieldName: 'Locale',
          label: 'Language',
          className: 'col-6',
          type: InputTypesEnum.TextReadOnly,
          hide: TemplateWindowDescription.shownIn(ShownPanelsEnum.FileUpload),
        },
        {
          // Never entered: it comes from the file and may not change
          fieldName: 'Type',
          label: 'Data Type',
          className: 'col-6',
          type: InputTypesEnum.TextReadOnly,
          hide: TemplateWindowDescription.shownIn(ShownPanelsEnum.FileUpload),
        },
        {
          // Re-derives the "Saved as text?" guess, which only applies to a new template
          fieldName: 'SelectedFile',
          label: 'Template File',
          className: 'col-12',
          type: InputTypesEnum.FileUploadField,
          acceptedTypes: TemplateWindowDescription.acceptedTypes,
          validityChecker: ValidityHelper.forRequiredFileField,
          onFieldChange: (editedItem) => {
            editedItem.IsStringContent = TemplateWindowDescription.inferIsStringContent(editedItem.SelectedFile?.name);
          },
          hide: TemplateWindowDescription.shownIn(ShownPanelsEnum.AddNewItem, ShownPanelsEnum.FileUpload),
        },
        {
          // Decided by the template's first file and inherited afterwards, so never editable
          fieldName: 'IsStringContent',
          label: 'Saved as text?',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.AlwaysFlawless,
          // What the flag decides is how the file is kept, which is why it is worth explaining here
          info:
            'How the file is kept: on for a file that is wording (a web page or an email body), which is' +
            ' stored as readable text; off for a file that is a document (a spreadsheet or a Word file),' +
            ' which is stored as the file itself. Set for you from the file you picked — change it only if' +
            ' the file should be treated the other way.',
          hide: (item) => !TemplateWindowDescription.isNamingNewTemplate(item),
        },
        {
          // The template's active state; a new one is always created active
          fieldName: 'IsActive',
          label: 'Active',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.AlwaysFlawless,
          hide: TemplateWindowDescription.shownIn(ShownPanelsEnum.EditItem),
        },
      ],
    },
  ];
}
